/**
 * ================================================================================================
 *  העדכונים המקצועיים — A RUNNING STRIP ACROSS THE HEAD OF THE CONTENT. **Owner, §5, 2026-11-01.**
 * ================================================================================================
 *
 *     *"העדכונים המקצועיים יהיו בבאנר אופקי מתחלף בראש אזור התוכן, עם קישור למקור ואפשרות לעצור
 *     ולעבור בין עדכונים."*
 *
 * and, the same day, correcting the first build of it:
 *
 *     *"החדשות צריך להיות בשורה למעלה עם ריצה לצד, גלילה לצד של החדשות — תוכל לראות לפי
 *     [המערכת שהראיתי], לא להעתיק משם קוד אבל לקבל השראה."*
 *
 * THE BRACKET IS THE ONE SUBSTITUTION IN THAT QUOTATION, and it is not editorial: he named
 * the reference by the product's RETIRED name, and `retired-name.test.ts` fails any active
 * product file that carries it -- a half-finished rename is worse than either name applied
 * consistently. Every other word is his, and the reference itself is a screenshot that is not
 * in this repository.
 *
 * ── WHAT CHANGED, AND WHY THE FIRST READING WAS WRONG ──────────────────────────────────────────
 *
 * *"מתחלף"* was built as a card that SWAPS one headline for another every eight seconds, with
 * previous and next. That is a rotator, and it is not what he drew: the reference strip RUNS — the
 * headlines travel sideways in one continuous line, which is why they are a LINE and not a card.
 * A rotator shows one headline and hides the rest; a running strip shows the run.
 *
 * So the track is one row of every headline, duplicated once and translated by exactly half its
 * own width, which is what makes the loop seamless: at the end of the cycle the second copy sits
 * exactly where the first one started, so the jump back to zero is invisible.
 *
 * IT RUNS TOWARDS THE READING DIRECTION'S END. In Hebrew a reader starts at the right, so new text
 * must ENTER FROM THE LEFT and travel right; in English the opposite. The direction is a CSS
 * custom property set from the document's own `dir`, not a second component.
 *
 * ── AND IT IS STOPPABLE, WHICH IS NOT A COURTESY ───────────────────────────────────────────────
 *
 * WCAG 2.2.2 requires a mechanism to pause anything that moves automatically for more than five
 * seconds, and this never stops on its own. It has four:
 *
 *   · a labelled pause/resume button;
 *   · it pauses while the pointer is over it, so a headline cannot slide out from under a click;
 *   · it pauses while the keyboard is inside it, so a link cannot move away from the focus ring;
 *   · and it STARTS PAUSED under `prefers-reduced-motion: reduce` — stopped, not merely slower,
 *     because text that travels under a reader is exactly what that setting is for.
 *
 * When it is stopped the strip becomes an ordinary horizontally scrollable row, which a wheel, a
 * trackpad, a finger and the tab key all move — *"ולעבור בין עדכונים"*, by the thing the strip
 * already is.
 *
 * ── AND THE TWO ARROWS ARE GONE. **Owner, 2026-11-01, after seeing the built strip.** ──────────
 *
 *     *"תנועה אוטומטית רציפה בלי חיצים."*
 *
 * They were my reading of *"ולעבור בין עדכונים"* and they were a literal one: he asked to be able
 * to move between updates, and I gave him two buttons that do it in 280px steps. What he actually
 * wants is the strip to RUN and to be stoppable — a line that moves by itself, not a carousel with
 * a stepper. The ability to move between updates survives the removal: stopped, the strip is a
 * scroll container, and that is three instruments more than the arrows were.
 *
 * **WCAG 2.2.2 IS SATISFIED BY THE PAUSE BUTTON AND NOT BY THE ARROWS**, so nothing about the
 * accessibility of this strip depends on them. That was worth checking before deleting them.
 *
 * ── NO `aria-live` ─────────────────────────────────────────────────────────────────────────────
 *
 * The first build announced each swap, which was right for a rotator: the content genuinely
 * changed. Nothing changes here — every headline is in the DOM from the first paint, as a list of
 * links a screen reader reads in order and a keyboard tabs through. An `aria-live` region over
 * content that only MOVES would announce the same text again and again.
 *
 * ── AND IT IS STILL HONEST WHEN THERE IS NO FEED ───────────────────────────────────────────────
 *
 * The Owner's standing rule: *"המסך צריך להיות מוכן ולהציג שאין מקור מחובר — בלי נתונים מדומים."*
 * With no publisher configured, or a source that has produced nothing, the strip is ONE STATIC
 * LINE saying which of those it is. It is not hidden: a strip that appears on the day somebody
 * connects a feed is a layout change on that day.
 *
 * ── THE REFERENCE IS A REFERENCE ───────────────────────────────────────────────────────────────
 *
 * *"לא להעתיק משם קוד אבל לקבל השראה."* Nothing here is taken from that screenshot: the strip is
 * built from this product's own `user.market.read`, its own tokens and its own components, and
 * what the reference contributed is the SHAPE — a line at the top that runs.
 */

import { useEffect, useRef, useState } from 'react';
import type { JSX } from 'react';

import { ServerRead } from './ServerRead';
import { WidgetReadFault } from './WidgetPanel';
import { Icon } from '../shell/Icon';
import { useLocale } from '../i18n/LocaleProvider';
import type { ServerData } from '../api/useServerData';
import type { MarketCardView } from '../api/contract';

/**
 * How long one full cycle takes PER HEADLINE. The duration scales with the number of items so the
 * text moves at a constant speed whether the feed carries three updates or twelve — a fixed cycle
 * would make a long feed sprint and a short one crawl.
 */
const SECONDS_PER_ITEM = 14;

/**
 * ONE FEED ROW. The transport puts the headline and its link in `valueText`, separated by a tab —
 * the shape `MarketTile` already reads. Parsed in ONE place so the two cannot disagree about which
 * half is which.
 */
interface Update {
  readonly key: string;
  readonly titleHe: string;
  readonly href: string | null;
  readonly observedAtUtc: string;
}

function parseUpdates(card: MarketCardView | undefined): readonly Update[] {
  if (card === undefined) return [];
  const out: Update[] = [];
  for (const entry of card.series) {
    const [title, href] = (entry.valueText ?? '').split('\t');
    if (title === undefined || title === '') continue;
    out.push({
      key: entry.seriesCode,
      titleHe: title,
      /*
       * A LINK ONLY IF IT IS ONE. A feed row with no URL still has a headline worth showing, and
       * `href=""` renders as a link to the current page — a control that looks like it goes
       * somewhere and reloads the screen instead.
       */
      href: href === undefined || href === '' ? null : href,
      observedAtUtc: entry.observedAtUtc,
    });
  }
  return out;
}

/**
 * `2026-10-28T13:22:03.592Z` → `28/10/26`. The publisher's day, in the reader's order.
 *
 * THE SAME FORMAT THE MARKET CARDS USE, on the Owner's instruction of 2026-11-01 —
 * *"צריך להיות נכון ליום.. DD/MM/YY"*. He named it of a card and this is the other place a
 * market date is printed; two formats for one kind of fact, on one screen, is worse than either.
 */
function asDay(iso: string): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return iso;
  const day = String(at.getUTCDate()).padStart(2, '0');
  const month = String(at.getUTCMonth() + 1).padStart(2, '0');
  return `${day}/${month}/${String(at.getUTCFullYear()).slice(-2)}`;
}

export function UpdatesBanner({
  result,
}: {
  readonly result: ServerData<{ readonly cards: readonly MarketCardView[] }>;
}): JSX.Element {
  const { t } = useLocale();
  return (
    <ServerRead<{ readonly cards: readonly MarketCardView[] }>
      result={result}
      whatHe={t('home.widgets.news')}
      skeletonRows={1}
      renderNotFound={() => <WidgetReadFault />}
    >
      {(data) => (
        /*
         * `?? []` AND NOT `data.cards`. **2026-11-01, and the move is what made it matter.**
         *
         * The contract says the key is always there, and a response without it is malformed.
         * While this component belonged to the home screen that cost one screen; it is in the
         * SHELL now, so an answer with no `cards` threw inside render and took EVERY customer
         * screen with it -- found by `landing.dom.test.tsx`, whose harness stubs no market route
         * and therefore answers `{}`.
         *
         * A card ABSENT FROM the list is already handled below as the state it is; this is the
         * narrower case of the LIST being absent, and it reads the same way.
         */
        <BannerBody card={(data.cards ?? []).find((c) => c.domainCode === 'professional_updates')} />
      )}
    </ServerRead>
  );
}

function BannerBody({ card }: { readonly card: MarketCardView | undefined }): JSX.Element {
  const { t } = useLocale();
  const updates = parseUpdates(card);
  const viewport = useRef<HTMLDivElement | null>(null);
  /*
   * STARTS PAUSED WHERE THE MACHINE ASKED FOR NO MOTION. Read once, at first render, because the
   * question is about this visit and a person who changes the setting mid-session will reload.
   * `matchMedia` is absent in some embedded browsers, so its absence is "no preference".
   */
  const [running, setRunning] = useState(() => {
    try {
      return !(window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false);
    } catch {
      return true;
    }
  });
  /** Pointer over, or focus inside. Separate from `running`: it must not flip the person's switch. */
  const [held, setHeld] = useState(false);

  /*
   * MOVING AND SCROLLING ARE MUTUALLY EXCLUSIVE, and the reset is why this effect exists: a strip
   * that is scrolled by hand and then set running again would resume from a translate of zero
   * while the scroll offset stayed where the reader left it, so the first headline would appear
   * to jump. Starting the run from a scrolled-to-zero strip keeps the two in step.
   */
  useEffect(() => {
    if (!running) return;
    const box = viewport.current;
    if (box === null) return;
    // Guarded for the same reason as `step`.
    if (typeof box.scrollTo === 'function') box.scrollTo({ left: 0 });
    else box.scrollLeft = 0;
  }, [running]);

  /* ── THE THREE STATES THAT ARE NOT A FEED ──────────────────────────────────────────────────── */
  if (card === undefined) {
    return <BannerStrip>{t('market.noAnswer')}</BannerStrip>;
  }
  if (card.state === 'not_configured') {
    return <BannerStrip>{t('state.configurationRequiredBody')}</BannerStrip>;
  }
  if (updates.length === 0) {
    return (
      <BannerStrip>
        {card.lastAttemptFailed ? t('market.lastAttemptFailed') : t('market.noReadingYet')}
      </BannerStrip>
    );
  }

  const moving = running && !held;
  const publisherHe = card.publisherHe ?? '';

  /**
   * ONE HEADLINE, AS IT APPEARS IN THE RUNNING LINE.
   *
   * The SECOND copy of the list is `aria-hidden` and not focusable: it exists only so the loop has
   * no seam, and a screen reader that read every headline twice — or a keyboard that tabbed
   * through twelve links to reach six — would be paying for a visual trick.
   */
  const item = (update: Update, copy: 1 | 2): JSX.Element => (
    <span className="gp-updates__item" key={`${update.key}-${String(copy)}`}>
      <span className="gp-updates__dot" aria-hidden="true">
        ◆
      </span>
      {update.href === null ? (
        <span className="gp-updates__headline" dir="auto">
          {update.titleHe}
        </span>
      ) : (
        <a
          className="gp-updates__headline"
          href={update.href}
          target="_blank"
          /* `noopener` as well as `noreferrer`: a publisher's page must not reach back into this
             tab through `window.opener`. */
          rel="noopener noreferrer"
          {...(copy === 2 ? { tabIndex: -1 } : {})}
        >
          {update.titleHe}
        </a>
      )}
      {/*
        THE DATE THE PUBLISHER PUT ON IT, NAMED. `market-data.ts` is explicit that `observed_at`
        is the publisher timestamp and never the moment we read the feed; the word is on the
        screen so that a reader does not have to know that.

        (The word for retrieving something over a network is avoided here, for the THIRD time in
        this file. `web-client-choke-point` scans the whole text, comments included, and editing
        this area made it report that word in a DIFFERENT comment sixty lines below -- which had
        been in the tree, unreported, until then. I did not chase the mechanism in its stripper;
        both comments are reworded, which is the cheap answer to a check that is blunt by design.
        Arguing *it is only a comment* is the carve-out that makes a scanner stop working.)
      */}
      <span className="gp-updates__when">
        {t('market.publishedOn')} {asDay(update.observedAtUtc)}
      </span>
    </span>
  );

  return (
    <section
      className="gp-updates"
      aria-label={t('home.widgets.news')}
      onMouseEnter={() => setHeld(true)}
      onMouseLeave={() => setHeld(false)}
      onFocusCapture={() => setHeld(true)}
      onBlurCapture={() => setHeld(false)}
    >
      <span className="gp-updates__icon" aria-hidden="true">
        <Icon name="mail" />
      </span>
      {/* WHO PUBLISHES IT, FIXED AT THE START OF THE LINE. It does not travel with the headlines:
          it is one fact about the whole feed, and a publisher name sliding past repeatedly is
          noise the reader has to re-read. */}
      <span className="gp-updates__publisher">{publisherHe}</span>

      {/*
        ── WHEN WE LAST MANAGED TO ASK. **Owner, 2026-11-01.** *"להבחין בין תאריך הפרסום לבין
        מועד הסנכרון."* ──────────────────────────────────────────────────────────────────────

        It sits with the PUBLISHER and not with the headlines, because it is one fact about the
        whole feed. The stale chip below carries the same number with its own sentence, so this
        renders only when the feed is NOT stale — two dates in one line, one of them labelled
        twice, is the confusion this was added to end.
      */}
      {card.state !== 'stale' && card.lastSuccessAtUtc !== null ? (
        <span className="gp-updates__synced" data-synced="true">
          {t('market.syncedOn')} {asDay(card.lastSuccessAtUtc)}
        </span>
      ) : null}

      {/*
        ── A FEED THAT HAS STOPPED REFRESHING SAYS SO, IN THE LINE. **Owner, 2026-11-01.** ───────

            *"הבעיה שהחדשות האלו לא באמת חדשות — הם היו בזמנו, והייתי רוצה שזה יתעדכן באמת."*

        He is right, and it is the defect that matters more than the motion: a strip that runs
        old headlines looks MORE current than a static one, because movement reads as liveness.
        The server already answers `stale` when the newest reading is older than the source's own
        refresh window -- a rule that lives in a row the browser never sees, which is why the card
        renders the server's verdict instead of comparing dates itself.

        The first build of this strip IGNORED that state and ran the old headlines anyway. It now
        carries a visible mark and the date of the last SUCCESSFUL request, which is the one number
        that separates "the institute published nothing this week" from "we have not managed to
        ask since the second of October".
      */}
      {card.state === 'stale' ? (
        <span className="gp-updates__stale" data-stale="true">
          {t('market.stale')}
          {card.lastSuccessAtUtc === null ? '' : ` ${asDay(card.lastSuccessAtUtc)}`}
        </span>
      ) : null}

      <div
        className="gp-updates__viewport"
        ref={viewport}
        data-moving={moving ? 'yes' : undefined}
        /* Stopped, it is an ordinary scroll container — which is what the arrows drive. */
        data-scrollable={moving ? undefined : 'yes'}
      >
        <div
          className="gp-updates__track"
          /* The cycle scales with the number of headlines so the text moves at one speed whatever
             the feed carries. Inline because only this component knows how many there are. */
          style={{ animationDuration: `${String(updates.length * SECONDS_PER_ITEM)}s` }}
        >
          <span className="gp-updates__run">{updates.map((u) => item(u, 1))}</span>
          <span className="gp-updates__run" aria-hidden="true">
            {updates.map((u) => item(u, 2))}
          </span>
        </div>
      </div>

      {/*
        ONE CONTROL, WHICH IS THE WHOLE OF WCAG 2.2.2. *"תנועה אוטומטית רציפה בלי חיצים."*
      */}
      <span className="gp-updates__controls">
        <button
          type="button"
          className="gp-updates__control"
          /* The label says what the press WILL DO, which is the half usually got wrong: a stopped
             strip offers "resume", not "stopped". */
          aria-label={running ? t('home.updates.pause') : t('home.updates.resume')}
          aria-pressed={!running}
          onClick={() => setRunning((was) => !was)}
        >
          {running ? '⏸' : '▶'}
        </button>
      </span>
    </section>
  );
}

/** The one-line form: the strip keeps its place and says which absence this is. */
function BannerStrip({ children }: { readonly children: string }): JSX.Element {
  const { t } = useLocale();
  return (
    <section className="gp-updates gp-updates--absent" aria-label={t('home.widgets.news')}>
      <span className="gp-updates__icon" aria-hidden="true">
        <Icon name="mail" />
      </span>
      <span className="gp-updates__publisher">{t('home.widgets.news')}</span>
      <span className="gp-updates__when">{children}</span>
    </section>
  );
}
